import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'umi';
import type { IHighPriorityEventItem } from '@op/components/ChartsEvents/types';
import { useSplitPaneKeyboard } from '@op/components/SplitPane';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getAnomalyTimeline,
  type IAnomalyTimelineParams,
  type IAnomalyTimelineItem,
  type IAnomalyTimelineResponse,
} from '@op/services/keyMetrics';
import type { IDetailQueryParams } from '../EventDetailSidebar/types.d';
import moment from 'moment';
import { getTagNameMap } from '../utils';

// 时间格式常量
const DETAIL_PARAMS_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * 根据日期类型获取时间格式
 */
function getTimeFormat(dateType?: string): string {
  if (dateType === 'hourly') {
    return 'YYYY-MM-DD HH:mm:ss';
  }
  return 'YYYY-MM-DD';
}

/**
 * 格式化时间以匹配图表 xAxis 格式
 */
function formatTimeKey(time: string, dateType?: string): string {
  const format = getTimeFormat(dateType);
  return moment(time).format(format);
}

/**
 * 根据时间点计算日粒度的时间范围
 * @param time 时间点（如 "2024-01-15"）
 * @returns { startTime, endTime } 格式化后的开始和结束时间
 */
function calculateDailyTimeRange(time: string): {
  startTime: string;
  endTime: string;
} {
  const m = moment(time);
  return {
    startTime: m.startOf('day').format(DETAIL_PARAMS_FORMAT),
    endTime: m.endOf('day').format(DETAIL_PARAMS_FORMAT),
  };
}

/** useHighPriorityEvents 参数类型 */
export interface IUseHighPriorityEventsParams {
  filter: IAnomalyTimelineParams;
}

export interface UseHighPriorityEventsReturn {
  /** 高优先级事件数据 */
  highPriorityEvents: Record<string, IHighPriorityEventItem[]>;
  /** 设置高优先级事件数据 */
  setHighPriorityEvents: React.Dispatch<
    React.SetStateAction<Record<string, IHighPriorityEventItem[]>>
  >;
  /** 选中的事件（仅用于 UI 高亮） */
  selectedEvent: IHighPriorityEventItem | null;
  /** 设置选中事件 */
  setSelectedEvent: React.Dispatch<
    React.SetStateAction<IHighPriorityEventItem | null>
  >;
  /** 侧边栏是否可见 */
  sidebarVisible: boolean;
  /** 显示侧边栏 */
  showSidebar: () => void;
  /** 关闭侧边栏并取消选中 */
  closeSidebar: () => void;
  /** 处理事件点击（仅更新选中态，不再设置详情数据） */
  handleEventClick: (event: IHighPriorityEventItem) => void;
  /** 清除所有事件的选中状态 */
  clearEventSelection: () => void;
  /** 详情请求参数 */
  detailParams: IDetailQueryParams | null;
  /** 带埋点的事件点击处理 */
  handleEventClickWithTracking: (event: IHighPriorityEventItem) => void;
  /** 柱子点击处理 */
  handleBarClick: (time: string) => void;
  /** 关闭侧边栏处理（重置所有状态） */
  handleCloseSidebar: () => void;
  /** 加载状态 */
  loading: boolean;
  /** 错误状态 */
  error: boolean;
  /** 重新请求 */
  refetch: () => void;
}

/**
 * 将 API 响应数据转换为 IHighPriorityEventItem 格式
 */
function transformToHighPriorityEvent(
  item: IAnomalyTimelineItem,
  index: number,
): IHighPriorityEventItem {
  const {
    start_date,
    end_date,
    overview_title,
    overview_contribution,
    overview_trend,
    factors,
    region: rawRegion,
  } = item;
  const region =
    String(rawRegion)?.toLowerCase() === 'all' ? 'Global' : rawRegion;
  const topFactors = factors?.slice(0, 3) || [];
  const trendSign = overview_trend === 'up' ? 1 : -1;
  const { tagNameZh, tagNameEn } = getTagNameMap(region as string);

  return {
    id: `${start_date}-${end_date}-${index}`,
    title: overview_title,
    name: overview_title,
    overview_title,
    start_time: start_date,
    end_time: end_date,
    ratio: trendSign * overview_contribution * 100,
    list: topFactors.map(f => f.name),
    factors: topFactors,
    allFactors: factors,
    selected: false,
    tagName: region as string,
    tagNameZh,
    tagNameEn,
  };
}

/**
 * 将 API 响应的时间线数据转换为按时间分组的 Record 格式
 * @param timeline 时间线数据
 * @param dateType 日期类型，用于确定时间格式
 */
function transformTimelineData(
  timeline: IAnomalyTimelineItem[],
  dateType?: string,
): Record<string, IHighPriorityEventItem[]> {
  const result: Record<string, IHighPriorityEventItem[]> = {};

  timeline.forEach((item, index) => {
    const eventItem = transformToHighPriorityEvent(item, index);
    const rawTime = item.time || item.end_date || item.start_date;
    // 格式化时间以匹配图表 xAxis 格式
    const timeKey = rawTime ? formatTimeKey(rawTime, dateType) : '';

    if (timeKey && !result[timeKey]) {
      result[timeKey] = [];
    }
    if (timeKey) {
      result[timeKey].push(eventItem);
    }
  });

  return result;
}

/**
 * 高优先级事件状态管理 Hook
 * 整合事件数据、选中状态、侧边栏和点击事件的处理逻辑
 * 详情数据由 EventDetailSidebar 内部通过 useEventDetail 统一管理
 */
export function useHighPriorityEvents(
  params: IUseHighPriorityEventsParams,
  autoFetch: boolean,
  onFetchDetailData?: (params: IDetailQueryParams) => void,
  setActiveRegion?: (region: string) => void,
): UseHighPriorityEventsReturn {
  const { filter } = params;
  const dispatch = useDispatch();
  const [highPriorityEvents, setHighPriorityEvents] = useState<
    Record<string, IHighPriorityEventItem[]>
  >({});
  const [selectedEvent, setSelectedEvent] =
    useState<IHighPriorityEventItem | null>(null);
  const [detailParams, setDetailParams] = useState<IDetailQueryParams | null>(
    null,
  );

  // 侧边栏状态 - 内部管理
  const [sidebarVisible, setSidebarVisible] = useState(false);

  // API 请求状态
  const { loading, wrapperCancelRequest, error } =
    useCancelRequestHook<IAnomalyTimelineResponse>();

  /**
   * 请求异常事件时间线数据
   * 注意：hourly 模式不支持高级事件，API 仅支持 daily/weekly/monthly
   */
  const fetchData = useCallback(() => {
    if (!autoFetch) return;
    if (!filter?.edition_unified_id) return;
    // hourly 和 monthly 模式不支持高级事件
    if (filter?.date_type === 'hourly' || filter?.date_type === 'monthly') {
      setHighPriorityEvents({});
      return;
    }

    wrapperCancelRequest(getAnomalyTimeline, filter)
      .then(data => {
        // 传递 date_type 以确保时间格式与图表 xAxis 一致
        const transformedData = transformTimelineData(
          data?.timeline || [],
          filter?.date_type,
        );
        setHighPriorityEvents(transformedData);
      })
      .catch(() => {
        setHighPriorityEvents({});
      });
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * 显示侧边栏，同时收起左侧菜单
   */
  const showSidebar = useCallback(() => {
    setSidebarVisible(true);
    dispatch({
      type: 'opinionMenu/toggleMenuExpand',
      payload: { menuExpand: false },
    });
  }, [dispatch]);

  /**
   * 取消所有事件的选中状态
   */
  const clearEventSelection = useCallback(() => {
    setHighPriorityEvents(prev => {
      const updated: typeof prev = {};
      Object.entries(prev).forEach(([date, events]) => {
        updated[date] = events.map(e => ({
          ...e,
          selected: false,
        }));
      });
      return updated;
    });
    setSelectedEvent(null);
  }, []);

  /**
   * 更新事件的选中状态
   */
  const updateEventSelection = useCallback((targetEventId: string | null) => {
    setHighPriorityEvents(prev => {
      const updated: typeof prev = {};
      Object.entries(prev).forEach(([date, events]) => {
        updated[date] = events.map(e => ({
          ...e,
          selected: targetEventId ? e.id === targetEventId : false,
        }));
      });
      return updated;
    });
  }, []);

  /**
   * 关闭侧边栏并取消选中
   */
  const closeSidebar = useCallback(() => {
    setSidebarVisible(false);
    dispatch({
      type: 'opinionMenu/toggleMenuExpand',
      payload: { menuExpand: true },
    });
    clearEventSelection();
  }, [clearEventSelection, dispatch]);

  /**
   * 统一点击处理函数（合并事件气泡点击和柱子点击）
   */
  const handleDetailOpen = useCallback(
    ({
      source,
      event,
      time,
    }: {
      source: 'event' | 'bar' | 'detail';
      event?: IHighPriorityEventItem;
      time?: string;
    }) => {
      if (event?.selected) {
        closeSidebar();
        return;
      }
      if (source === 'event' && event) {
        // 事件气泡点击：更新选中态
        const {
          id,
          start_time,
          end_time,
          overview_title,
          title,
          name,
          tagName,
        } = event;
        // 转换为与 bar 模式一致的日期时间格式
        const startTime = moment(start_time)
          .startOf('day')
          .format(DETAIL_PARAMS_FORMAT);
        const endTime = moment(end_time)
          .endOf('day')
          .format(DETAIL_PARAMS_FORMAT);
        setSelectedEvent(event);
        updateEventSelection(id);
        const detailParams: IDetailQueryParams = {
          source: 'event',
          edition_unified_id: filter.edition_unified_id,
          start_time: startTime,
          end_time: endTime,
          overview_title: overview_title || title,
          data_type: 'overview',
          region: tagName,
        };
        setDetailParams(detailParams);
        onFetchDetailData?.(detailParams);
        buriedBtnPoint('KMMS_EventClick', {
          event_id: id,
          event_name: name || title,
          startTime,
          endTime,
        });
      } else if (source === 'bar' && time) {
        // 柱子点击：仅支持日粒度，非日粒度不触发
        if (filter.date_type && filter.date_type !== 'daily') {
          return;
        }

        // 基于时间点计算日粒度时间范围
        const { startTime, endTime } = calculateDailyTimeRange(time);
        // 没有事件时清除选中态
        setSelectedEvent(null);
        updateEventSelection(null);

        const detailParams: IDetailQueryParams = {
          source: 'bar',
          edition_unified_id: filter.edition_unified_id,
          start_time: startTime,
          end_time: endTime,
          data_type: 'daily',
        };
        setDetailParams(detailParams);
        onFetchDetailData?.(detailParams);
        buriedBtnPoint('KMMS_BarClick', { time });
      }
      showSidebar();
    },
    [
      filter,
      closeSidebar,
      updateEventSelection,
      showSidebar,
      onFetchDetailData,
    ],
  );

  /**
   * 处理事件点击
   * 1. 更新选中状态（用于 UI 高亮）
   * 2. 设置选中事件引用（用于传递给外部 handleDetailOpen）
   * 3. 显示侧边栏
   */
  const handleEventClick = useCallback(
    (event: IHighPriorityEventItem) => {
      // 1. 更新选中状态
      setHighPriorityEvents(prev => {
        const updated: typeof prev = {};
        Object.entries(prev).forEach(([date, events]) => {
          updated[date] = events.map(e => ({
            ...e,
            selected: e.id === event.id,
          }));
        });
        return updated;
      });

      // 2. 设置选中事件（仅用于 UI 高亮和传递给外部）
      setSelectedEvent(event);

      // 3. 显示侧边栏
      showSidebar();
    },
    [showSidebar],
  );

  /**
   * 带埋点的事件点击处理
   * - 点击已选中的事件：仅切换区域 tab，不触发 handleDetailOpen（避免关闭侧边栏）
   * - 点击其他事件：正常走完整流程
   */
  const handleEventClickWithTracking = useCallback(
    (event: IHighPriorityEventItem, source: 'event' | 'detail' = "event") => {
      if (event?.tagName) {
        setActiveRegion?.(event?.tagName);
      }
      console.log(selectedEvent, "selectedEvent", event)
      // 已选中的事件再次点击，只切换 tab 不重复打开/关闭侧边栏
      if (selectedEvent?.id === event?.id) {
        return;
      }
      handleDetailOpen({ source, event });
    },
    [handleDetailOpen, selectedEvent],
  );

  /**
   * 柱子点击处理
   */
  const handleBarClick = useCallback(
    (time: string) => {
      handleDetailOpen({ source: 'bar', time });
    },
    [handleDetailOpen],
  );

  /**
   * 关闭侧边栏处理（重置所有状态）
   */
  const handleCloseSidebar = useCallback(() => {
    closeSidebar();
    setSelectedEvent(null);
    setDetailParams(null);
    updateEventSelection(null);
  }, [closeSidebar, updateEventSelection]);

  // 添加 ESC 键关闭功能
  useSplitPaneKeyboard(sidebarVisible, closeSidebar);

  return {
    highPriorityEvents,
    setHighPriorityEvents,
    selectedEvent,
    setSelectedEvent,
    sidebarVisible,
    showSidebar,
    closeSidebar,
    handleEventClick,
    clearEventSelection,
    detailParams,
    handleEventClickWithTracking,
    handleBarClick,
    handleCloseSidebar,
    loading,
    error,
    refetch: fetchData,
  };
}
